import type { GuardianResult } from "@/services/api/guardiansv2/types";
import type { ScopeLabelCache } from "./useScopeLabelCache";
import { EntityRichText } from "../shared/EntityRichText";
import type { EntityTextScope } from "../shared/entityTags";

export interface PersonaPanelProps {
  result: GuardianResult | null | undefined;
  label: string;
  running?: boolean;
  entityScope?: EntityTextScope;
  scopeLabels?: ScopeLabelCache;
}

function BulletList({
  items,
  title,
  entityScope,
  scopeLabels,
}: {
  items: string[];
  title: string;
  entityScope: EntityTextScope;
  scopeLabels?: ScopeLabelCache;
}) {
  if (!items.length) return null;
  return (
    <details className="group">
      <summary className="cursor-pointer text-xs font-medium text-slate-300">
        {title} ({items.length})
      </summary>
      <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-slate-400">
        {items.map((item, i) => (
          <li key={i}>
            <EntityRichText text={item} scope={entityScope} scopeLabels={scopeLabels} />
          </li>
        ))}
      </ul>
    </details>
  );
}

/** Persona verdict block inside the audit inspector. */
export function PersonaPanel({
  result,
  label,
  running = false,
  entityScope = {},
  scopeLabels,
}: PersonaPanelProps) {
  if (running) {
    return (
      <div
        className="rounded-lg border border-indigo-500/25 bg-indigo-500/10 px-4 py-6 text-center"
        data-testid="persona-panel-running"
      >
        <p className="text-sm font-medium text-indigo-200">Audit in progress…</p>
        <p className="mt-1 text-xs text-indigo-300/80">
          {label} is evaluating this entity. Results will appear here when complete.
        </p>
      </div>
    );
  }

  if (!result) {
    return (
      <p className="text-sm text-slate-500" data-testid="persona-panel-empty">
        {label} has not run yet. Use the rerun controls below to queue this persona.
      </p>
    );
  }

  const leadText =
    result.explanation?.trim() ||
    ((result.concerns?.length ?? 0) > 0 ? result.concerns![0] : "");

  return (
    <div className="space-y-3" data-testid="persona-panel">
      {leadText ? (
        <div className="text-sm text-slate-300">
          <EntityRichText text={leadText} scope={entityScope} scopeLabels={scopeLabels} />
        </div>
      ) : (
        <p className="text-sm text-slate-500" data-testid="persona-panel-no-explanation">
          No written explanation was recorded for this evaluation.
        </p>
      )}
      <BulletList items={result.strengths} title="Strengths" entityScope={entityScope} scopeLabels={scopeLabels} />
      <BulletList items={result.concerns} title="Concerns" entityScope={entityScope} scopeLabels={scopeLabels} />
      <BulletList
        items={result.recommendations}
        title="Recommendations"
        entityScope={entityScope}
        scopeLabels={scopeLabels}
      />
    </div>
  );
}
